<template>
    <div
        v-if="showPicker"
        class="kgr-grid kgr-grid--full kgr-grid--small-fit kgr-grid--gutters kgr-grid--bigGutters"
    >
        <div class="kgr-grid__cell">
            <div class=" kgr-inputGroup">
                <div
                    v-t="'Freigabe für Abteilungen'"
                    class="kgr-inputGroup__label"
                ></div>
                <p
                    v-if="!departmentsAvailable"
                    v-t="'Keine Abteilungen vorhanden.'"
                ></p>
                <kgr-multi-select
                    v-else
                    v-model="selectedDepartments"
                    :options="flattenDepartments"
                    display="name"
                    searchable
                    :disabled="!clearingEditable"
                    @post-input="selectedDepartmentsChange()"
                />
            </div>
        </div>

        <div
            class="kgr-grid__cell"
        >
            <div class=" kgr-inputGroup">
                <div
                    v-t="'Freigabe für Teams'"
                    class="kgr-inputGroup__label"
                ></div>
                <p
                    v-if="!teamsAvailable"
                    v-t="'Keine Teams vorhanden.'"
                ></p>
                <kgr-multi-select
                    v-else
                    v-model="selectedTeams"
                    :options="fullTeamsList"
                    display="name"
                    searchable
                    :disabled="!clearingEditable"
                    @post-input="selectedTeamsChange($event)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import MultiSelect from '../../common/form/select/MultiSelect.vue';

export default {
    components: {
        'kgr-multi-select': MultiSelect,
    },
    props: {
        clearingFormId: {
            type: String,
            required: true,
        },
        clearingDepartmentsId: {
            type: String,
            required: true,
        },
        clearingTeamsId: {
            type: String,
            required: true,
        },
        clearingMembers: {
            type: String,
            required: true,
        },
        clearingEmployees: {
            type: String,
            required: true,
        },
        departments: {
            type: Array,
            required: true,
        },
        teams: {
            type: Array,
            required: true,
        },
        clearingEditable: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    data() {
        return {
            showPicker: false,
            flattenDepartments: [],
            sortedTeams: [],
            selectedDepartments: [],
            selectedTeams: [],
        };
    },
    computed: {
        departmentsAvailable() {
            return this.flattenDepartments.length !== 0;
        },
        teamsAvailable() {
            return this.teams.length !== 0;
        },
        fullTeamsList() {
            return this.filteredTeams
                .map((t) => {
                    return {
                        id: t.id,
                        name: `${t.departmentName} / ${t.name}`,
                    };
                });
        },
        filteredTeams() {
            return this.sortedTeams
                .filter((t) => t.isBasicDataEditable)
                .filter((t) => this.selectedTeams.indexOf(t) === -1);
        },
        filteredDepartments() {
            return this.departments
                .filter((d) => this.selectedDepartments.indexOf(d) === -1);
        },
    },
    mounted() {
        this.flattenDepartmentsAndChildDepartments(this.filteredDepartments);
        // before checkShowPicker(), which now needs filteredTeams -- and that reads sortedTeams
        this.sortTeams(this.teams);
        this.markSelectedDepartmentsAndTeams();
        this.checkShowPicker();

        const clearingInputs = this.jQuery_clearingInputs();
        if (clearingInputs) {
            clearingInputs.change(this.checkShowPicker);
            this.$once('hook:destroyed', () => clearingInputs.off('change', this.checkShowPicker));
        }
    },
    methods: {
        sortTeams(teams) {
            // copy first: Array.sort() is in-place, so this used to reorder the caller's array -- which is
            // the `teams` prop, i.e. the data provider's own list
            this.sortedTeams = [...teams].sort((a, b) => (`${a.departmentName} / ${a.name}`).toLowerCase().localeCompare((`${b.departmentName} / ${b.name}`).toLowerCase()));
        },
        checkShowPicker() {
            const clearingInput = this.jQuery_clearingInput();
            if (clearingInput && (clearingInput.val() === this.clearingMembers || clearingInput.val() === this.clearingEmployees)) {
                this.showPicker = true;
                this.preselectSoleTeam();
            } else {
                this.showPicker = false;
                this.deselectDepartmentsAndTeams();
            }
        },
        /**
         * A "certain ..." clearing is rejected without at least one department or team
         * (News::validateClearing), so a user with exactly one team and no department has no other input
         * that saves -- selecting it is not a choice, it is the only way out of the form.
         *
         * Never do this for a department: a department is broader than its teams, so defaulting one would
         * publish wider than the user asked. Only a lone team is safe, because a team is always the
         * narrower of the two.
         *
         * Runs on mount and when the clearing radio changes, never on every selection change -- otherwise
         * deselecting the team would immediately put it back and the user could not clear the field.
         */
        preselectSoleTeam() {
            if (!this.clearingEditable) {
                return;
            }
            if (this.selectedDepartments.length !== 0 || this.selectedTeams.length !== 0) {
                return;
            }
            if (this.fullTeamsList.length !== 1) {
                return;
            }

            // an entry of fullTeamsList, which is what MultiSelect pushes into the model on a click
            this.selectedTeams = [this.fullTeamsList[0]];
            this.selectedTeamsChange();
        },
        flattenDepartmentsAndChildDepartments(departments) {
            departments.sort((a, b) => (`${a.departmentName} / ${a.name}`).toLowerCase().localeCompare((`${b.departmentName} / ${b.name}`).toLowerCase()));
            departments.forEach((department) => {
                if (department.isEditable) {
                    this.flattenDepartments.push({
                        id: department.id,
                        name: department.name,
                    });
                }
                this.getChildDepartments(department);
            });
        },
        getChildDepartments(department) {
            department.childDepartments.sort((a, b) => ((a.name).toLowerCase() < (b.name).toLowerCase()) ? -1 : ((a.name).toLowerCase() > (b.name).toLowerCase()) ? 1 : 0);
            department.childDepartments.forEach((childDepartment) => {
                if (childDepartment.isEditable) {
                    this.flattenDepartments.push({
                        id: childDepartment.id,
                        name: childDepartment.hierarchyPath,
                    });
                }
                this.getChildDepartments(childDepartment);
            });
        },
        selectedDepartmentsChange() {
            this.jQuery_departmentOptions().removeAttr('selected');
            this.selectedDepartments.forEach((department) => {
                const departmentOption = this.jQuery_departmentOption(department.id);
                if (departmentOption) {
                    departmentOption.attr('selected', true);
                }
            });
        },
        selectedTeamsChange() {
            this.jQuery_teamOptions().removeAttr('selected');
            this.selectedTeams.forEach((team) => {
                const teamOption = this.jQuery_teamOption(team.id);
                if (teamOption) {
                    teamOption.attr('selected', true);
                }
            });
        },
        markSelectedDepartmentsAndTeams() {
            this.selectedDepartments = [];
            this.selectedTeams = [];

            this.jQuery_departmentOptionSelected().each((_, option) => {
                const department = this.flattenDepartments.find((availableDepartment) => `${availableDepartment.id}` === $(option).val());
                if (department) {
                    this.selectedDepartments.push(department);
                }
            });

            this.jQuery_teamOptionSelected().each((_, option) => {
                const team = this.teams.find((availableTeam) => `${availableTeam.id}` === $(option).val());
                if (team) {
                    this.selectedTeams.push(team);
                }
            });
        },
        deselectDepartmentsAndTeams() {
            this.jQuery_departmentOptions().removeAttr('selected');
            this.jQuery_teamOptions().removeAttr('selected');
            this.markSelectedDepartmentsAndTeams();
        },
        jQuery_departmentOptions() {
            return this.jQuery_options(this.jQuery_departmentsSelect());
        },
        jQuery_departmentOption(value) {
            return this.jQuery_option(value, this.jQuery_departmentsSelect());
        },
        jQuery_departmentOptionSelected() {
            return this.jQuery_optionSelected(this.jQuery_departmentsSelect());
        },
        jQuery_teamOptions() {
            return this.jQuery_options(this.jQuery_teamsSelect());
        },
        jQuery_teamOption(value) {
            return this.jQuery_option(value, this.jQuery_teamsSelect());
        },
        jQuery_teamOptionSelected() {
            return this.jQuery_optionSelected(this.jQuery_teamsSelect());
        },
        jQuery_optionSelected(select) {
            return select.children('option:selected');
        },
        jQuery_option(value, select) {
            return select.children(`option[value="${value}"]`);
        },
        jQuery_options(select) {
            return select.children('option');
        },
        jQuery_departmentsSelect() {
            return $(`#${this.clearingDepartmentsId}`);
        },
        jQuery_teamsSelect() {
            return $(`#${this.clearingTeamsId}`);
        },
        jQuery_clearingInput() {
            return $(`#${this.clearingFormId} input[type=radio]:checked`);
        },
        jQuery_clearingInputs() {
            return $(`#${this.clearingFormId} input[type=radio]`);
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
